import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowDown, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useHowieAnalytics } from '../hooks/useHowieAnalytics';
import { supabase, getValidAccessToken, handleInvalidSession, HowieConversation } from '../services/supabase';
import { renderHowieMarkdown } from '../components/HowieMarkdown';
import { getHowieWsUrl } from '../config/howieService';
import { HowieComposer } from '../components/HowieComposer';
import { WebSources, normalizeWebActivity, type WebActivity } from '../components/WebSources';
import { useResumableChat } from '@lightning/hooks';
import {
  clearPendingChatCommand,
  getChatContentScrollSignal,
  loadPendingChatCommand,
  savePendingChatCommand,
  type ResumableTurnSnapshot,
} from '@lightning/utils';

interface Message extends WebActivity {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

interface TurnCompletionPayload extends WebActivity {
  turn_id?: string;
  assistant_content?: string;
  content?: string;
}

interface ChatNavigationState {
  conversationId?: string;
  messages?: HowieConversation['messages'];
  newConversation?: boolean;
  prefillMessage?: string;
  autoSendMessage?: string;
  autoSendDisplayMessage?: string;
}

interface AutoSendPayload {
  content: string;
  displayContent: string;
}

const HOWIE_WS_URL = getHowieWsUrl();

// Marker the backend uses to denote an inline citation (see server.py).
// NOTE: this constant was referenced by the analytics instrumentation but never
// defined — at runtime that threw `ReferenceError: CITATION_MARKER is not
// defined` inside sendMessageContent *before* ws.send(), so Ask messages were
// never actually sent (eternal spinner, no reply). Defining it fixes the core bug.
const CITATION_MARKER = ':::how-citation';

// Client-side backstop. The server bounds the Ask turn (~75s) and sends a
// graceful notice on timeout, but if the socket drops mid-turn that notice
// never arrives — so we also watch for inactivity here and never leave the
// user staring at an endless spinner. Reset on every chunk of progress.
const RESPONSE_INACTIVITY_MS = 90_000;
const MAX_RECONNECT_DELAY_MS = 16_000;

const CHAT_STARTERS = [
  'A decision I’m weighing',
  'A relationship I’m navigating',
  'A moment I want to understand',
] as const;

const HOME_QUOTES = [
  'HOW is not a status. It is a life practice of building muscle.',
  'HOW is generative. It generates innovation.',
  'Trust is the only legal performance-enhancing drug.',
  'Extending trust before verifying releases something.',
  'People meet you at the level you treat them.',
] as const;

const HOME_QUOTE_STORAGE_KEY = 'howie:home_quote_index';

function getSessionHomeQuote() {
  let index = Math.floor(Math.random() * HOME_QUOTES.length);

  try {
    if (typeof window !== 'undefined') {
      const storedIndex = Number(window.sessionStorage.getItem(HOME_QUOTE_STORAGE_KEY));
      if (Number.isInteger(storedIndex) && storedIndex >= 0 && storedIndex < HOME_QUOTES.length) {
        index = storedIndex;
      } else {
        window.sessionStorage.setItem(HOME_QUOTE_STORAGE_KEY, String(index));
      }
    }
  } catch (error) {
    if (!(typeof DOMException !== 'undefined' && error instanceof DOMException)) throw error;
  }

  return HOME_QUOTES[index] ?? HOME_QUOTES[0];
}

function createAutoSendPayload(state: ChatNavigationState | null): AutoSendPayload | null {
  const content = state?.autoSendMessage?.trim();
  if (!content) return null;

  return {
    content,
    displayContent: state?.autoSendDisplayMessage?.trim() || content,
  };
}

export default function ChatPage() {
  const { session, user } = useAuth();
  const resumableSurface = `general:${user?.id || 'anonymous'}`;
  const { track } = useHowieAnalytics();
  const location = useLocation();
  const navigate = useNavigate();
  const [homeQuote] = useState(getSessionHomeQuote);

  // Stable ref to the analytics tracker so the WebSocket effect (which only
  // depends on the access token) always calls the latest closure without
  // re-subscribing the socket on every render.
  const trackRef = useRef(track);
  useEffect(() => { trackRef.current = track; }, [track]);

  // WS health bookkeeping (hook:'chat' discriminator).
  const wsReconnectAttemptsRef = useRef(0);
  const wsWasConnectedRef = useRef(false);

  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [canSteer, setCanSteer] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [conversationId, setConversationId] = useState<string | null>(null);
  const {
    activeTurn,
    beginTurn,
    updateTurn,
    acceptSnapshot,
    finishTurn,
    draft: input,
    setDraft: setInput,
    clearDraft,
  } = useResumableChat({ app: 'howie', surface: resumableSurface, conversationId });
  const [pendingAutoSendMessage, setPendingAutoSendMessage] = useState<AutoSendPayload | null>(null);
  // User-visible error for the current turn (timeout, dropped socket, server error).
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const chatContentScrollSignal = useMemo(
    () => getChatContentScrollSignal(messages, streamingContent),
    [messages, streamingContent],
  );

  const wsRef = useRef<WebSocket | null>(null);
  const messageScrollRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const shouldFollowLatestRef = useRef(true);
  const sessionIdRef = useRef<string>(crypto.randomUUID());
  // Ref to track conversation ID synchronously (avoids race condition with state)
  const conversationIdRef = useRef<string | null>(null);
  // Ref to prevent duplicate saves while a save is in progress
  const isSavingRef = useRef<boolean>(false);
  const autoSentMessageRef = useRef<string | null>(null);
  // Inactivity watchdog for an in-flight turn + the last message (for retry).
  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastUserMessageRef = useRef<string | null>(null);
  // Mirror isLoading so socket callbacks can read "is a turn in flight" without
  // re-subscribing the WebSocket effect.
  const isLoadingRef = useRef(false);
  // Authoritative copy of the streamed text so socket callbacks can read the
  // current partial without a functional state updater (updaters must stay pure).
  const streamingContentRef = useRef('');
  const activeTurnRef = useRef(activeTurn);
  useEffect(() => { activeTurnRef.current = activeTurn; }, [activeTurn]);

  const updateActiveTurn = useCallback((updates: Parameters<typeof updateTurn>[0]) => {
    if (activeTurnRef.current) {
      activeTurnRef.current = {
        ...activeTurnRef.current,
        ...updates,
        updatedAt: new Date().toISOString(),
      };
    }
    updateTurn(updates);
  }, [updateTurn]);

  const finishActiveTurn = useCallback(() => {
    activeTurnRef.current = null;
    finishTurn();
  }, [finishTurn]);

  // Arm/clear the inactivity watchdog so a stalled or dropped turn can never
  // leave the composer spinning forever.
  const clearWatchdog = useCallback(() => {
    if (watchdogRef.current) {
      clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
  }, []);

  const armWatchdog = useCallback(() => {
    clearWatchdog();
    watchdogRef.current = setTimeout(() => {
      console.warn('[Howie] Response watchdog fired — reconnecting to recover turn state');
      setErrorMessage(
        "The reply paused longer than expected. I'm reconnecting and restoring it.",
      );
      trackRef.current('chat_response_timeout', { hook: 'chat' });
      wsRef.current?.close(4000, 'response inactivity');
    }, RESPONSE_INACTIVITY_MS);
  }, [clearWatchdog]);

  // Clean up the watchdog on unmount.
  useEffect(() => clearWatchdog, [clearWatchdog]);
  useEffect(() => { isLoadingRef.current = isLoading; }, [isLoading]);

  // Handle loading conversation from navigation state
  useEffect(() => {
    const state = location.state as ChatNavigationState | null;
    const autoSendPayload = createAutoSendPayload(state);

    if (state?.newConversation) {
      // Start fresh conversation
      setMessages([]);
      finishActiveTurn();
      clearPendingChatCommand('howie', resumableSurface);
      setConversationId(null);
      conversationIdRef.current = null;
      sessionIdRef.current = crypto.randomUUID();
      autoSentMessageRef.current = null;
      clearDraft();
      setInput(state.prefillMessage ?? '');
      if (autoSendPayload) {
        setPendingAutoSendMessage(autoSendPayload);
      }
      // Clear the state to prevent re-triggering
      navigate('/chat', { replace: true, state: null });
    } else if (state?.conversationId && state?.messages) {
      // Load existing conversation
      const loadedMessages: Message[] = state.messages.map((m, idx) => ({
        id: `loaded-${idx}`,
        role: m.role,
        content: m.content,
        timestamp: m.timestamp || new Date().toISOString(),
        ...(m.role === 'assistant' ? normalizeWebActivity(m) : undefined),
      }));
      setMessages(loadedMessages);
      setConversationId(state.conversationId);
      conversationIdRef.current = state.conversationId;
      sessionIdRef.current = crypto.randomUUID();
      // Clear the state to prevent re-triggering
      navigate('/chat', { replace: true, state: { conversationId: state.conversationId } });
    } else if (state?.prefillMessage) {
      setInput(state.prefillMessage);
      navigate('/chat', { replace: true, state: null });
    } else if (autoSendPayload) {
      setPendingAutoSendMessage(autoSendPayload);
      navigate('/chat', { replace: true, state: null });
    }
  }, [clearDraft, finishActiveTurn, location.state, navigate, resumableSurface, setInput]);

  // Connect continuously while authenticated. A socket is delivery only: an
  // interrupted turn remains active and is reconciled after the next auth.
  useEffect(() => {
    if (!session?.access_token) return;

    let disposed = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const completeTurn = (data: TurnCompletionPayload, persistLegacy: boolean) => {
      const turn = activeTurnRef.current;
      if (data.turn_id && turn && data.turn_id !== turn.turnId) return;
      clearWatchdog();
      setErrorMessage(null);
      const content = String(data.assistant_content ?? data.content ?? streamingContentRef.current ?? '');
      const assistantId = data.turn_id || turn?.turnId
        ? `${data.turn_id || turn?.turnId}:assistant`
        : crypto.randomUUID();
      const webActivity = normalizeWebActivity(data);
      if (content.trim()) {
        setMessages((prev) => {
          if (prev.some((message) => message.id === assistantId)) return prev;
          const updated = [...prev, {
            id: assistantId,
            role: 'assistant' as const,
            content,
            timestamp: new Date().toISOString(),
            ...webActivity,
          }];
          if (persistLegacy) void saveConversation(updated);
          return updated;
        });
      }
      streamingContentRef.current = '';
      setStreamingContent('');
      setIsLoading(false);
      clearPendingChatCommand('howie', resumableSurface);
      finishActiveTurn();
    };

    const applySnapshotToUi = (snapshot: ResumableTurnSnapshot) => {
      const turn = activeTurnRef.current;
      if (!turn || snapshot.turn_id !== turn.turnId) return;
      activeTurnRef.current = {
        ...turn,
        conversationId: snapshot.conversation_id || turn.conversationId,
        lastSequence: Math.max(turn.lastSequence, snapshot.last_sequence || 0),
        status: snapshot.status,
        updatedAt: new Date().toISOString(),
      };
      acceptSnapshot(snapshot);
      if (snapshot.conversation_id) {
        conversationIdRef.current = snapshot.conversation_id;
        setConversationId(snapshot.conversation_id);
      }
      const userContent = snapshot.user_message?.content?.trim();
      if (userContent) {
        setMessages((prev) => prev.some((message) => message.id === snapshot.client_message_id)
          ? prev
          : [...prev, {
              id: snapshot.client_message_id,
              role: 'user',
              content: userContent,
              timestamp: snapshot.user_message.timestamp || new Date().toISOString(),
            }]);
      }
      streamingContentRef.current = snapshot.assistant_content || '';
      setStreamingContent(streamingContentRef.current);
      if (snapshot.status === 'completed') {
        completeTurn(snapshot, false);
      } else if (snapshot.status === 'stopped' || snapshot.status === 'interrupted' || snapshot.status === 'failed') {
        const partial = snapshot.assistant_content || '';
        if (partial.trim()) {
          setMessages((prev) => prev.some((message) => message.id === `${snapshot.turn_id}:assistant`)
            ? prev
            : [...prev, {
                id: `${snapshot.turn_id}:assistant`,
                role: 'assistant',
                content: partial,
                timestamp: new Date().toISOString(),
              }]);
        }
        streamingContentRef.current = '';
        setStreamingContent('');
        setIsLoading(false);
        setErrorMessage(snapshot.error || (snapshot.status === 'failed' ? 'That reply could not be completed.' : null));
        clearPendingChatCommand('howie', resumableSurface);
        finishActiveTurn();
      } else {
        setIsLoading(true);
        armWatchdog();
      }
    };

    const connect = () => {
      if (disposed) return;
      const current = wsRef.current?.readyState;
      if (current === WebSocket.OPEN || current === WebSocket.CONNECTING) return;

      const ws = new WebSocket(`${HOWIE_WS_URL}/${sessionIdRef.current}`);
      wsRef.current = ws;

      ws.onopen = async () => {
        if (disposed || wsRef.current !== ws) return;
        const accessToken = await getValidAccessToken();
        if (!accessToken || disposed || ws.readyState !== WebSocket.OPEN) {
          ws.close();
          return;
        }
        ws.send(JSON.stringify({ type: 'auth', token: accessToken }));
      };

      ws.onmessage = (event) => {
        if (disposed || wsRef.current !== ws) return;
        // The service sends a discriminated protocol with legacy and durable
        // shapes during migration, so fields are narrowed inside each case.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let data: Record<string, any>;
        try {
          data = JSON.parse(event.data);
        } catch (parseError) {
          console.error('[Howie] Failed to parse WebSocket message:', parseError);
          return;
        }

        const turn = activeTurnRef.current;
        if (data.turn_id && turn && data.turn_id !== turn.turnId) return;

        switch (data.type) {
          case 'auth_success': {
            setIsConnected(true);
            if (wsReconnectAttemptsRef.current > 0) {
              trackRef.current('ws_reconnected', { hook: 'chat', attempts: wsReconnectAttemptsRef.current });
            } else {
              trackRef.current('ws_connected', { hook: 'chat' });
            }
            wsReconnectAttemptsRef.current = 0;
            wsWasConnectedRef.current = true;
            const pending = loadPendingChatCommand<Record<string, unknown>>('howie', resumableSurface);
            if (pending) ws.send(JSON.stringify(pending));
            const active = activeTurnRef.current;
            if (active) {
              ws.send(JSON.stringify({
                type: 'sync_turn',
                turn_id: active.turnId,
                after_sequence: active.lastSequence,
              }));
            }
            break;
          }
          case 'auth_failed':
            setIsConnected(false);
            trackRef.current('ws_auth_failed', { hook: 'chat' });
            void handleInvalidSession().finally(() => ws.close());
            break;
          case 'message_ack':
            {
              const pending = loadPendingChatCommand<Record<string, unknown>>('howie', resumableSurface);
              const pendingTurnId = pending?.type === 'steer_turn' ? pending.next_turn_id : pending?.turn_id;
              if (!pendingTurnId || !data.turn_id || pendingTurnId === data.turn_id) {
                clearPendingChatCommand('howie', resumableSurface);
              }
            }
            if (data.turn_id && data.turn_id !== activeTurnRef.current?.turnId) break;
            setCanSteer(data.capabilities?.steer === true);
            if (data.conversation_id) {
              conversationIdRef.current = data.conversation_id;
              setConversationId(data.conversation_id);
            }
            updateActiveTurn({
              status: 'running',
              conversationId: data.conversation_id || activeTurnRef.current?.conversationId || null,
              lastSequence: Math.max(activeTurnRef.current?.lastSequence || 0, Number(data.last_sequence ?? data.sequence) || 0),
            });
            break;
          case 'turn_snapshot':
            applySnapshotToUi(data as ResumableTurnSnapshot);
            break;
          case 'tool_use':
            armWatchdog();
            break;
          case 'chunk': {
            const sequence = Number(data.sequence) || 0;
            if (sequence && sequence <= (activeTurnRef.current?.lastSequence || 0)) break;
            if (sequence) updateActiveTurn({ lastSequence: sequence, status: 'running' });
            armWatchdog();
            setErrorMessage(null);
            streamingContentRef.current += String(data.content || '');
            setStreamingContent(streamingContentRef.current);
            break;
          }
          case 'chunk_reset':
            armWatchdog();
            streamingContentRef.current = '';
            setStreamingContent('');
            break;
          case 'turn_complete':
            completeTurn(data, false);
            break;
          case 'complete':
            completeTurn(data, true);
            break;
          case 'turn_stopped': {
            clearWatchdog();
            const partial = String(data.assistant_content ?? data.content ?? streamingContentRef.current ?? '');
            if (partial.trim()) {
              const assistantId = `${data.turn_id || activeTurnRef.current?.turnId}:assistant`;
              setMessages((prev) => prev.some((message) => message.id === assistantId)
                ? prev
                : [...prev, {
                    id: assistantId,
                    role: 'assistant',
                    content: partial,
                    timestamp: new Date().toISOString(),
                  }]);
            }
            streamingContentRef.current = '';
            setStreamingContent('');
            setIsLoading(false);
            clearPendingChatCommand('howie', resumableSurface);
            finishActiveTurn();
            break;
          }
          case 'error': {
            clearWatchdog();
            const partial = streamingContentRef.current;
            if (partial.trim()) {
              setMessages((prev) => [...prev, {
                id: `${data.turn_id || activeTurnRef.current?.turnId || crypto.randomUUID()}:assistant`,
                role: 'assistant',
                content: partial,
                timestamp: new Date().toISOString(),
              }]);
            }
            streamingContentRef.current = '';
            setStreamingContent('');
            setErrorMessage(typeof data.content === 'string' && data.content.trim()
              ? data.content
              : 'Something went wrong generating that reply. Please try again.');
            setIsLoading(false);
            clearPendingChatCommand('howie', resumableSurface);
            finishActiveTurn();
            break;
          }
        }
      };

      ws.onclose = (event) => {
        if (wsRef.current !== ws) return;
        wsRef.current = null;
        setIsConnected(false);
        clearWatchdog();
        if (isLoadingRef.current || activeTurnRef.current) {
          setErrorMessage("The connection was interrupted. I'm reconnecting and restoring the reply.");
        }
        if (wsWasConnectedRef.current) {
          trackRef.current('ws_disconnected', { hook: 'chat', code: event.code, reason: event.reason || null });
          wsWasConnectedRef.current = false;
        }
        if (!disposed) {
          wsReconnectAttemptsRef.current += 1;
          const delay = Math.min(1000 * (2 ** Math.max(0, wsReconnectAttemptsRef.current - 1)), MAX_RECONNECT_DELAY_MS);
          reconnectTimer = setTimeout(connect, delay);
        }
      };

      ws.onerror = (error) => console.error('[Howie] WebSocket error:', error);
    };

    connect();
    return () => {
      disposed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      const socket = wsRef.current;
      wsRef.current = null;
      socket?.close();
    };
  }, [session?.access_token, acceptSnapshot, armWatchdog, clearWatchdog, finishActiveTurn, resumableSurface, updateActiveTurn]);

  // Follow the conversation only while the reader is already at the bottom.
  // Once they scroll up, streaming must not pull the page away from what they
  // are reading; the explicit return control restores following when wanted.
  useEffect(() => {
    if (shouldFollowLatestRef.current) {
      messagesEndRef.current?.scrollIntoView({
        behavior: streamingContent ? 'auto' : 'smooth',
        block: 'end',
      });
    }
  }, [chatContentScrollSignal, streamingContent]);

  const handleConversationScroll = useCallback(() => {
    const container = messageScrollRef.current;
    if (!container) return;

    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    const isNearBottom = distanceFromBottom < 96;
    shouldFollowLatestRef.current = isNearBottom;
    setShowJumpToLatest(!isNearBottom);
  }, []);

  const jumpToLatest = useCallback(() => {
    shouldFollowLatestRef.current = true;
    setShowJumpToLatest(false);
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, []);

  // Save conversation to database
  const saveConversation = async (updatedMessages: Message[]) => {
    if (!user) return;

    // Prevent duplicate saves while one is in progress
    if (isSavingRef.current) {
      console.log('[Howie] Save already in progress, skipping');
      return;
    }

    const messagesForDb = updatedMessages.map((m) => ({
      role: m.role,
      content: m.content,
      timestamp: m.timestamp,
      // Keep an answer's web searches and sources with it in history.
      ...(m.webSearches?.length ? { webSearches: m.webSearches } : {}),
      ...(m.citations?.length ? { citations: m.citations } : {}),
    }));

    // Get last user message as preview
    const lastUserMsg = [...updatedMessages].reverse().find((m) => m.role === 'user');
    const lastMessage = lastUserMsg?.content.slice(0, 100) || '';

    try {
      isSavingRef.current = true;

      // Use ref for synchronous check (state may be stale in callback)
      const currentConversationId = conversationIdRef.current || conversationId;

      if (currentConversationId) {
        // Update existing conversation
        const { error } = await supabase
          .from('howie_general_conversations')
          .update({
            messages: messagesForDb,
            last_message: lastMessage,
            updated_at: new Date().toISOString(),
          })
          .eq('id', currentConversationId)
          .eq('user_id', user.id);

        if (error) throw error;
      } else {
        // Create new conversation
        const { data, error } = await supabase
          .from('howie_general_conversations')
          .insert({
            user_id: user.id,
            messages: messagesForDb,
            last_message: lastMessage,
          })
          .select('id')
          .single();

        if (error) throw error;
        if (data) {
          // Update both ref and state
          conversationIdRef.current = data.id;
          setConversationId(data.id);
          trackRef.current('conversation_created', { surface: 'general' });
        }
      }
    } catch (error) {
      console.error('[Howie] Error saving conversation:', error);
    } finally {
      isSavingRef.current = false;
    }
  };

  const sendMessageContent = useCallback((content: string, displayContent?: string): boolean => {
    const trimmedContent = content.trim();
    const visibleContent = (displayContent ?? trimmedContent).trim();
    if (!trimmedContent) return false;

    shouldFollowLatestRef.current = true;
    setShowJumpToLatest(false);

    // Don't silently drop the message onto a closed/closing socket — sending to
    // a non-OPEN socket strands the spinner with no reply. Surface it and bail.
    const ws = wsRef.current;
    if (!isConnected || !ws || ws.readyState !== WebSocket.OPEN) {
      setErrorMessage("I'm not connected right now. Give it a moment to reconnect, then try again.");
      return false;
    }

    if (isLoading && (!canSteer || activeTurnRef.current?.status === 'queued')) {
      setErrorMessage('Your previous message is still being delivered. This draft will stay here until it is acknowledged.');
      return false;
    }

    if (isLoading && activeTurnRef.current) {
      const interruptedTurn = activeTurnRef.current;
      const nextTurn = beginTurn(conversationIdRef.current || conversationId);
      activeTurnRef.current = nextTurn;
      const command = {
        type: 'steer_turn',
        turn_id: interruptedTurn.turnId,
        content: trimmedContent,
        next_turn_id: nextTurn.turnId,
        client_message_id: nextTurn.clientMessageId,
        conversation_id: conversationIdRef.current || conversationId,
        mode: 'general',
      };
      savePendingChatCommand('howie', resumableSurface, command);
      try {
        ws.send(JSON.stringify(command));
      } catch (err) {
        console.error('[Howie] Failed to steer active turn:', err);
        ws.close();
      }
      const partial = streamingContentRef.current;
      setMessages((prev) => {
        const withPartial = partial.trim() && !prev.some((message) => message.id === `${interruptedTurn.turnId}:assistant`)
          ? [...prev, {
              id: `${interruptedTurn.turnId}:assistant`,
              role: 'assistant' as const,
              content: partial,
              timestamp: new Date().toISOString(),
            }]
          : prev;
        return [...withPartial, {
          id: nextTurn.clientMessageId,
          role: 'user' as const,
          content: visibleContent,
          timestamp: new Date().toISOString(),
        }];
      });
      streamingContentRef.current = '';
      setStreamingContent('');
      lastUserMessageRef.current = trimmedContent;
      clearDraft();
      setErrorMessage(null);
      armWatchdog();
      return true;
    }

    const resumableTurn = activeTurnRef.current?.status === 'queued'
      ? activeTurnRef.current
      : beginTurn(conversationIdRef.current || conversationId);
    activeTurnRef.current = resumableTurn;

    const userMsg: Message = {
      id: resumableTurn.clientMessageId,
      role: 'user',
      content: visibleContent,
      timestamp: new Date().toISOString(),
    };

    // Build conversation history for context (excludes the turn we're sending).
    const conversationHistory = messages.map((m) => ({
      role: m.role,
      content: m.content,
    }));

    const command = {
      type: 'message',
      content: trimmedContent,
      mode: 'general',
      conversation_id: conversationIdRef.current || conversationId,
      conversation_history: conversationHistory,
      turn_id: resumableTurn.turnId,
      client_message_id: resumableTurn.clientMessageId,
    };
    savePendingChatCommand('howie', resumableSurface, command);

    try {
      ws.send(JSON.stringify(command));
    } catch (err) {
      console.error('[Howie] Failed to send message:', err);
      setErrorMessage('The connection changed while sending. Reconnecting now.');
      ws.close();
    }

    // Only commit UI state once the send actually went out.
    lastUserMessageRef.current = trimmedContent;
    setErrorMessage(null);
    setMessages((prev) => [...prev, userMsg]);
    clearDraft();
    setIsLoading(true);
    armWatchdog();

    track('chat_message_sent', {
      mode: 'general',
      length: userMsg.content.length,
      has_citations: userMsg.content.includes(CITATION_MARKER),
    });

    return true;
  }, [armWatchdog, beginTurn, canSteer, clearDraft, conversationId, isConnected, isLoading, messages, resumableSurface]);

  const handleRetry = useCallback(() => {
    const last = lastUserMessageRef.current;
    setErrorMessage(null);
    if (last) sendMessageContent(last);
  }, [sendMessageContent]);

  const handleSend = () => {
    sendMessageContent(input);
  };

  useEffect(() => {
    if (!pendingAutoSendMessage || !isConnected || isLoading) return;
    if (autoSentMessageRef.current === pendingAutoSendMessage.content) return;

    autoSentMessageRef.current = pendingAutoSendMessage.content;
    const didSend = sendMessageContent(
      pendingAutoSendMessage.content,
      pendingAutoSendMessage.displayContent,
    );
    if (didSend) {
      setPendingAutoSendMessage(null);
    }
  }, [isConnected, isLoading, pendingAutoSendMessage, sendMessageContent]);

  const firstName = user?.user_metadata?.given_name
    || user?.user_metadata?.full_name?.split(' ')[0]
    || user?.user_metadata?.name?.split(' ')[0]
    || '';

  const isEmpty = messages.length === 0 && !streamingContent && !isLoading;

  const errorBanner = errorMessage ? (
    <div
      role="alert"
      className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-how-accent-warm/40 bg-how-accent-warm/10 px-4 py-2.5"
    >
      <span className="text-sm text-how-text-secondary">{errorMessage}</span>
      {lastUserMessageRef.current && (
        <button
          type="button"
          onClick={handleRetry}
          disabled={!isConnected || isLoading}
          className="shrink-0 text-sm font-semibold text-how-accent-warm underline-offset-2 hover:underline disabled:opacity-50"
        >
          Try again
        </button>
      )}
    </div>
  ) : null;

  return (
    <div
      className={`absolute inset-0 flex flex-col ${isEmpty ? 'dojo-scroll overflow-y-auto' : ''}`}
      style={{ background: 'linear-gradient(180deg, var(--how-paper-soft) 0%, var(--color-how-page-wash) 100%)' }}
    >

      {/* Empty state: centered greeting + composer */}
      {isEmpty && (
        <div className="relative flex flex-1 flex-col px-4">
          <blockquote className="absolute left-1/2 top-6 w-[calc(100%-2rem)] max-w-2xl -translate-x-1/2 text-center sm:top-8">
            <p className="font-voice text-[13px] leading-5 text-how-text-secondary/85">
              &ldquo;{homeQuote}&rdquo;
              <cite className="ml-1.5 font-body text-[11px] not-italic text-how-text-secondary/85">
                &mdash; Dov Seidman
              </cite>
            </p>
          </blockquote>

          <div className="flex flex-1 items-center justify-center pb-4 pt-20 sm:pt-24">
            <div className="w-full max-w-xl">
              <h1 className="mb-6 text-center font-body text-[28px] font-medium leading-tight tracking-[-0.035em] text-how-space-deep sm:text-[32px]">
                {firstName ? <>
                  <span className="block break-words sm:inline">Hi {firstName},</span>
                  <span className="sm:hidden">What’s on your mind?</span>
                  <span className="hidden sm:inline"> what’s on your mind?</span>
                </> : 'What’s on your mind?'}
              </h1>
              <HowieComposer
                value={input}
                onChange={setInput}
                onSend={handleSend}
                disabled={!isConnected}
                sending={isLoading}
                allowSendWhileSending={canSteer}
                placeholder="Ask Howie..."
                minRows={2}
                className="min-h-[116px] items-start py-4"
              />
              <div className="mx-auto mt-6 w-full max-w-[30rem] space-y-1.5" aria-label="Conversation starters">
                {CHAT_STARTERS.map((starter) => (
                  <button
                    key={starter}
                    type="button"
                    onClick={() => setInput(starter)}
                    className="group flex min-h-9 w-full items-center justify-between rounded-[10px] border border-how-accent-warm/10 bg-white/20 px-3.5 py-2 text-left text-[13px] leading-5 text-how-text-secondary/85 transition-colors hover:border-how-accent-warm/20 hover:bg-white/45 hover:text-how-space-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep/30"
                  >
                    <span>{starter}</span>
                    <ChevronRight
                      size={14}
                      aria-hidden="true"
                      className="text-gray-300 transition-transform group-hover:translate-x-0.5 group-hover:text-gray-400"
                    />
                  </button>
                ))}
              </div>
              {errorBanner}
              {!isConnected && (
                <p className="mt-3 text-sm text-how-text-secondary">
                  Connecting to Howie...
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Chat layout: messages + bottom composer */}
      {!isEmpty && (
        <>
          <div
            ref={messageScrollRef}
            onScroll={handleConversationScroll}
            className="flex-1 overflow-y-auto px-4 pb-8 pt-7 [overflow-anchor:none] sm:pt-9"
          >
            <div className="mx-auto max-w-2xl space-y-8">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    data-testid={message.role === 'assistant' ? 'assistant-message' : undefined}
                    className={message.role === 'user'
                      ? 'max-w-[88%] rounded-2xl rounded-br-md border border-how-space-deep/[0.04] bg-how-rose/35 px-4 py-3 sm:max-w-[78%] sm:px-5'
                      : 'w-full max-w-[680px] border-l border-how-space-deep/10 py-1 pl-5 sm:pl-6'
                    }
                  >
                    {message.role === 'assistant' ? (
                      <>
                        <div className="prose prose-sm max-w-none prose-dark prose-chat prose-p:leading-[1.65]">
                          {renderHowieMarkdown(message.content)}
                        </div>
                        <WebSources webSearches={message.webSearches} citations={message.citations} />
                      </>
                    ) : (
                      <p className="font-body text-[15px] leading-6 text-how-text-on-paper sm:text-[16px] sm:leading-[26px]">{message.content}</p>
                    )}
                  </div>
                </div>
              ))}

              {streamingContent && (
                <div className="flex justify-start">
                  <div
                    data-testid="assistant-message-streaming"
                    aria-live="polite"
                    className="w-full max-w-[680px] border-l border-how-space-deep/10 py-1 pl-5 sm:pl-6"
                  >
                    <div className="prose prose-sm max-w-none prose-dark prose-chat prose-p:leading-[1.65]">
                      {renderHowieMarkdown(streamingContent)}
                    </div>
                  </div>
                </div>
              )}

              {isLoading && !streamingContent && (
                <div className="flex justify-start">
                  <div
                    role="status"
                    className="flex items-center gap-2.5 border-l border-how-space-deep/10 py-2 pl-5 text-sm text-how-text-secondary/70 sm:pl-6"
                  >
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-how-accent-warm" aria-hidden="true" />
                    <span>Considering&hellip;</span>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          <div className="relative bg-[linear-gradient(180deg,transparent_0%,var(--how-page-wash)_24%)] px-4 pb-9 pt-7 sm:pb-10">
            {showJumpToLatest && (
              <button
                type="button"
                onClick={jumpToLatest}
                className="absolute left-1/2 top-0 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-black/[0.06] bg-white px-3 py-1.5 text-xs text-how-text-secondary shadow-sm transition-colors hover:text-how-space-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep/30"
              >
                <ArrowDown size={13} aria-hidden="true" />
                Latest
              </button>
            )}
            <HowieComposer
              value={input}
              onChange={setInput}
              onSend={handleSend}
              disabled={!isConnected}
              sending={isLoading}
              allowSendWhileSending={canSteer}
              placeholder="Ask Howie..."
              className="mx-auto max-w-2xl"
            />
            <div className="mx-auto max-w-2xl">{errorBanner}</div>
            {!isConnected && (
              <p className="text-center text-how-text-secondary text-sm mt-2">
                Connecting to Howie...
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
